"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Pair = void 0;
const chain_1 = require("@zenlink-interface/chain");
const currency_1 = require("@zenlink-interface/currency");
const math_1 = require("@zenlink-interface/math");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const errors_1 = require("../errors");
const Fee_1 = require("../Fee");
const computePairAddress_1 = require("./computePairAddress");
const constants_1 = require("./constants");
class Pair {
    static getAddress(tokenA, tokenB) {
        return (0, computePairAddress_1.computePairAddress)({
            factoryAddress: constants_1.FACTORY_ADDRESS[tokenA.chainId],
            tokenA,
            tokenB,
        });
    }
    constructor(AmountA, AmountB, PairAddress) {
        this.swapGasCost = math_1.JSBI.BigInt(60000);
        this.minLiquidity = math_1.JSBI.BigInt(1000);
        this.fee = Fee_1.Fee.DEFAULT;
        const Amounts = AmountA.currency.sortsBefore(AmountB.currency) // does safety checks
            ? [AmountA, AmountB]
            : [AmountB, AmountA];
        const chainId = Amounts[0].currency.chainId;
        this.liquidityToken = new currency_1.Token({
            chainId,
            address: this.pairAddress = PairAddress || Pair.getAddress(Amounts[0].currency, Amounts[1].currency),
            decimals: (chainId === chain_1.ParachainId.AMPLITUDE || chainId === chain_1.ParachainId.PENDULUM) ? 12 : 18,
            symbol: 'ZLK-LP',
            name: 'Zenlink LP Token',
        });
        this.tokenAmounts = Amounts;
    }
    /**
     * Returns true if the token is either token0 or token1
     * @param token to check
     */
    involvesToken(token) {
        return token.equals(this.token0) || token.equals(this.token1);
    }
    /**
     * Returns the current mid price of the pair in terms of token0, i.e. the ratio of reserve1 to reserve0
     */
    get token0Price() {
        const result = this.tokenAmounts[1].divide(this.tokenAmounts[0]);
        return new currency_1.Price(this.token0, this.token1, result.denominator, result.numerator);
    }
    /**
     * Returns the current mid price of the pair in terms of token1, i.e. the ratio of reserve0 to reserve1
     */
    get token1Price() {
        const result = this.tokenAmounts[0].divide(this.tokenAmounts[1]);
        return new currency_1.Price(this.token1, this.token0, result.denominator, result.numerator);
    }
    /**
     * Return the price of the given token in terms of the other token in the pair.
     * @param token token to return price of
     */
    priceOf(token) {
        (0, tiny_invariant_1.default)(this.involvesToken(token), 'TOKEN');
        return token.equals(this.token0) ? this.token0Price : this.token1Price;
    }
    /**
     * Returns the chain ID of the tokens in the pair.
     */
    get chainId() {
        return this.token0.chainId;
    }
    get token0() {
        return this.tokenAmounts[0].currency;
    }
    get token1() {
        return this.tokenAmounts[1].currency;
    }
    get reserve0() {
        return this.tokenAmounts[0];
    }
    get reserve1() {
        return this.tokenAmounts[1];
    }
    reserveOf(token) {
        (0, tiny_invariant_1.default)(this.involvesToken(token), 'TOKEN');
        return token.equals(this.token0) ? this.reserve0 : this.reserve1;
    }
    pathOf(token) {
        (0, tiny_invariant_1.default)(this.involvesToken(token), 'TOKEN');
        return {
            stable: false,
            input: token,
            output: token.equals(this.token0) ? this.token1 : this.token0,
            pair: this,
        };
    }
    getOutputAmount(inputAmount) {
        (0, tiny_invariant_1.default)(this.involvesToken(inputAmount.currency), 'TOKEN');
        if (math_1.JSBI.equal(this.reserve0.quotient, math_1.ZERO) || math_1.JSBI.equal(this.reserve1.quotient, math_1.ZERO))
            throw new errors_1.InsufficientReservesError();
        const inputReserve = this.reserveOf(inputAmount.currency);
        const outputReserve = this.reserveOf(inputAmount.currency.equals(this.token0) ? this.token1 : this.token0);
        const inputAmountWithFee = math_1.JSBI.multiply(inputAmount.quotient, math_1._997);
        const numerator = math_1.JSBI.multiply(inputAmountWithFee, outputReserve.quotient);
        const denominator = math_1.JSBI.add(math_1.JSBI.multiply(inputReserve.quotient, math_1._1000), inputAmountWithFee);
        const outputAmount = currency_1.Amount.fromRawAmount(inputAmount.currency.equals(this.token0) ? this.token1 : this.token0, math_1.JSBI.divide(numerator, denominator));
        if (math_1.JSBI.equal(outputAmount.quotient, math_1.ZERO))
            throw new errors_1.InsufficientInputAmountError();
        return [outputAmount, new Pair(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount), this.pairAddress)];
    }
    getInputAmount(outputAmount) {
        (0, tiny_invariant_1.default)(this.involvesToken(outputAmount.currency), 'TOKEN');
        if (math_1.JSBI.equal(this.reserve0.quotient, math_1.ZERO)
            || math_1.JSBI.equal(this.reserve1.quotient, math_1.ZERO)
            || math_1.JSBI.greaterThanOrEqual(outputAmount.quotient, this.reserveOf(outputAmount.currency).quotient)) {
            throw new errors_1.InsufficientReservesError();
        }
        const outputReserve = this.reserveOf(outputAmount.currency);
        const inputReserve = this.reserveOf(outputAmount.currency.equals(this.token0) ? this.token1 : this.token0);
        const numerator = math_1.JSBI.multiply(math_1.JSBI.multiply(inputReserve.quotient, outputAmount.quotient), math_1._1000);
        const denominator = math_1.JSBI.multiply(math_1.JSBI.subtract(outputReserve.quotient, outputAmount.quotient), math_1._997);
        const inputAmount = currency_1.Amount.fromRawAmount(outputAmount.currency.equals(this.token0) ? this.token1 : this.token0, math_1.JSBI.add(math_1.JSBI.divide(numerator, denominator), math_1.ONE));
        return [inputAmount, new Pair(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount), this.pairAddress)];
    }
    getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB) {
        (0, tiny_invariant_1.default)(totalSupply.currency.equals(this.liquidityToken), 'LIQUIDITY');
        const tokenAmounts = tokenAmountA.currency.sortsBefore(tokenAmountB.currency) // does safety checks
            ? [tokenAmountA, tokenAmountB]
            : [tokenAmountB, tokenAmountA];
        (0, tiny_invariant_1.default)(tokenAmounts[0].currency.equals(this.token0) && tokenAmounts[1].currency.equals(this.token1), 'TOKEN');
        let liquidity;
        if (math_1.JSBI.equal(totalSupply.quotient, math_1.ZERO)) {
            liquidity = math_1.JSBI.subtract((0, math_1.sqrt)(math_1.JSBI.multiply(tokenAmounts[0].quotient, tokenAmounts[1].quotient)), this.minLiquidity);
        }
        else {
            const amount0 = math_1.JSBI.divide(math_1.JSBI.multiply(tokenAmounts[0].quotient, totalSupply.quotient), this.reserve0.quotient);
            const amount1 = math_1.JSBI.divide(math_1.JSBI.multiply(tokenAmounts[1].quotient, totalSupply.quotient), this.reserve1.quotient);
            liquidity = math_1.JSBI.lessThanOrEqual(amount0, amount1) ? amount0 : amount1;
        }
        if (!math_1.JSBI.greaterThan(liquidity, math_1.ZERO))
            throw new errors_1.InsufficientInputAmountError();
        return currency_1.Amount.fromRawAmount(this.liquidityToken, liquidity);
    }
    getLiquidityValue(token, totalSupply, liquidity, feeOn = false, kLast) {
        (0, tiny_invariant_1.default)(this.involvesToken(token), 'TOKEN');
        (0, tiny_invariant_1.default)(totalSupply.currency.equals(this.liquidityToken), 'TOTAL_SUPPLY');
        (0, tiny_invariant_1.default)(liquidity.currency.equals(this.liquidityToken), 'LIQUIDITY');
        (0, tiny_invariant_1.default)(math_1.JSBI.lessThanOrEqual(liquidity.quotient, totalSupply.quotient), 'LIQUIDITY');
        let totalSupplyAdjusted;
        if (!feeOn) {
            totalSupplyAdjusted = totalSupply;
        }
        else {
            (0, tiny_invariant_1.default)(!!kLast, 'K_LAST');
            const kLastParsed = math_1.JSBI.BigInt(kLast);
            if (!math_1.JSBI.equal(kLastParsed, math_1.ZERO)) {
                const rootK = (0, math_1.sqrt)(math_1.JSBI.multiply(this.reserve0.quotient, this.reserve1.quotient));
                const rootKLast = (0, math_1.sqrt)(kLastParsed);
                if (math_1.JSBI.greaterThan(rootK, rootKLast)) {
                    const numerator = math_1.JSBI.multiply(totalSupply.quotient, math_1.JSBI.subtract(rootK, rootKLast));
                    const denominator = math_1.JSBI.add(math_1.JSBI.multiply(rootK, math_1.FIVE), rootKLast);
                    const feeLiquidity = math_1.JSBI.divide(numerator, denominator);
                    totalSupplyAdjusted = totalSupply.add(currency_1.Amount.fromRawAmount(this.liquidityToken, feeLiquidity));
                }
                else {
                    totalSupplyAdjusted = totalSupply;
                }
            }
            else {
                totalSupplyAdjusted = totalSupply;
            }
        }
        return currency_1.Amount.fromRawAmount(token, math_1.JSBI.divide(math_1.JSBI.multiply(liquidity.quotient, this.reserveOf(token).quotient), totalSupplyAdjusted.quotient));
    }
}
exports.Pair = Pair;
